import type {
  UserMoveConflict,
  UserMoveConflictPayload,
  UserMoveConflictType,
  UserMoveRevocationSummary,
} from '@smartsash/api-client';

/**
 * The vocabulary of the move flow: how a 409 body is read, and what order the revocation counts
 * are shown in.
 *
 * Both halves exist because the endpoint answers a *typed* payload on a failure status.
 * `POST /api/User/{institutionId}/{userId}/move` returns `UserMoveConflictPayload` as the `data`
 * of a 409 envelope, and Orval types only the 200 — so the narrowing lives here, in one place,
 * rather than being repeated by every component that renders a branch of it.
 */

/** Duck-typed view of `NormalizedApiError`; imported structurally so page tests may mock the client. */
interface ApiErrorWithBody {
  status?: number;
  body?: unknown;
}

const STATUS_CONFLICT = 409;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Read the conflict payload out of a rejected move call.
 *
 * The path is `error.body.data`, not a resolved value: `customInstance` unwraps to `data` only on
 * the success path, so a 409 rejects with the whole envelope kept on `body`. Anything that does
 * not carry a recognised `conflictType` is treated as "not a move conflict" and left to the
 * generic error handling — a sixth member added server-side must not render as a blank panel.
 */
export function readMoveConflict(error: unknown): UserMoveConflictPayload | undefined {
  if (!isRecord(error)) return undefined;
  const { status, body } = error as ApiErrorWithBody;
  if (status !== STATUS_CONFLICT || !isRecord(body)) return undefined;

  const data: unknown = body.data;
  if (!isRecord(data)) return undefined;

  const conflict: unknown = data.conflict;
  if (!isRecord(conflict) || !isKnownConflictType(conflict.conflictType)) return undefined;

  return data;
}

/**
 * i18n key per conflict, with a generic fallback for a value this build does not know.
 *
 * `Record<UserMoveConflictType, …>` is what makes the set exhaustive: the day the backend adds a
 * sixth member, this object fails `typecheck` rather than an administrator meeting a blank panel.
 * Only the *type* is imported — the generated enum object is a runtime value, and page tests that
 * mock `@smartsash/api-client` wholesale would have to grow an entry for it.
 */
export const CONFLICT_MESSAGE_KEYS: Record<UserMoveConflictType, string> = {
  EmailAlreadyExistsInTarget: 'users.move.conflicts.emailExists',
  RfidSerialAlreadyExistsInTarget: 'users.move.conflicts.rfidSerialExists',
  RfidCardSharedWithOtherUser: 'users.move.conflicts.rfidCardShared',
  ActiveHoodSession: 'users.move.conflicts.activeSession',
  ConfirmationRequired: 'users.move.conflicts.confirmationRequired',
};

/** The same set as a runtime list, so membership can be tested without the generated enum. */
const KNOWN_CONFLICT_TYPES: readonly string[] = Object.keys(CONFLICT_MESSAGE_KEYS);

function isKnownConflictType(value: unknown): value is UserMoveConflictType {
  return typeof value === 'string' && KNOWN_CONFLICT_TYPES.includes(value);
}

/** `confirmRevocations: false` is answered with this conflict — the preview, not a failure. */
export function isConfirmationRequired(conflict: UserMoveConflict | undefined): boolean {
  return conflict?.conflictType === 'ConfirmationRequired';
}

export const CONFLICT_FALLBACK_KEY = 'users.move.conflicts.unknown';

export function conflictMessageKey(conflict: UserMoveConflict | undefined): string {
  const type = conflict?.conflictType;
  return type != null && type in CONFLICT_MESSAGE_KEYS
    ? CONFLICT_MESSAGE_KEYS[type]
    : CONFLICT_FALLBACK_KEY;
}

/**
 * A move needs somewhere to move to. With one institution in reach there is no destination, so
 * every entry point hides rather than opening a screen whose select would be empty — which is
 * also what keeps the action away from an institution admin, who has exactly one.
 */
export const MIN_INSTITUTIONS_TO_MOVE = 2;

/** What a revocation row belongs to: access that ends, data that travels, data that stays. */
export type RevocationGroup = 'revoked' | 'follows' | 'staysBehind';

export interface RevocationRow {
  field: keyof UserMoveRevocationSummary;
  group: RevocationGroup;
  labelKey: string;
}

/**
 * Every counter the payload carries, in reading order: what the move takes away first, then what
 * travels with the user, then what the old institution keeps. All twelve are listed deliberately —
 * a zero is information ("nothing to lose here"), not noise to hide.
 */
export const REVOCATION_ROWS: readonly RevocationRow[] = [
  {
    field: 'accessScopesRevoked',
    group: 'revoked',
    labelKey: 'users.move.revocations.accessScopes',
  },
  { field: 'assignmentsRevoked', group: 'revoked', labelKey: 'users.move.revocations.assignments' },
  { field: 'permissionsRevoked', group: 'revoked', labelKey: 'users.move.revocations.permissions' },
  { field: 'sessionsTerminated', group: 'revoked', labelKey: 'users.move.revocations.sessions' },
  {
    field: 'accountTokensPurged',
    group: 'revoked',
    labelKey: 'users.move.revocations.accountTokens',
  },
  {
    field: 'calendarParticipationsRemoved',
    group: 'revoked',
    labelKey: 'users.move.revocations.calendarParticipations',
  },
  { field: 'remindersRemoved', group: 'revoked', labelKey: 'users.move.revocations.reminders' },
  {
    field: 'applicationStatesRemoved',
    group: 'revoked',
    labelKey: 'users.move.revocations.applicationStates',
  },
  { field: 'accountsMoved', group: 'follows', labelKey: 'users.move.revocations.accounts' },
  { field: 'rfidCardsMoved', group: 'follows', labelKey: 'users.move.revocations.rfidCards' },
  {
    field: 'certificationsLeftBehind',
    group: 'staysBehind',
    labelKey: 'users.move.revocations.certifications',
  },
  {
    field: 'maintenanceRemindersLeftBehind',
    group: 'staysBehind',
    labelKey: 'users.move.revocations.maintenanceReminders',
  },
] as const;

export const REVOCATION_GROUP_TITLE_KEYS: Record<RevocationGroup, string> = {
  revoked: 'users.move.groups.revoked',
  follows: 'users.move.groups.follows',
  staysBehind: 'users.move.groups.staysBehind',
};
